import type { SensorAggregate, MonthDailyMeans, YoYMatrix } from "@weather-deck/shared";
import { el } from "./dom";
import { doyOf, ordinal } from "./monthRanking";

/** ~13 weeks — a calendar quarter's worth of days, but as a trailing window rather than Jan/Apr/Jul/Oct-anchored. */
export const QUARTER_WINDOW_DAYS = 91;

export interface QuarterRankRow {
  year: number;
  mean: number;
  days: number;
  rank: number;
  isCurrent: boolean;
}

export interface QuarterRanking {
  windowDays: number;
  windowLabel: string; // e.g. "1 May – 30 Jul"
  currentYear: number;
  rows: QuarterRankRow[];
  currentRow: QuarterRankRow | null;
  comparedDays: number;
  partial: boolean;
}

function berlinPartsOf(d: Date): { year: number; month: number; day: number } {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: "Europe/Berlin", year: "numeric", month: "numeric", day: "numeric"
  }).formatToParts(d);
  const num = (t: string) => parseInt(parts.find((p) => p.type === t)!.value, 10);
  return { year: num("year"), month: num("month"), day: num("day") };
}

/**
 * The trailing `windowDays` calendar dates ending at (year, month, day)
 * inclusive, as UTC-noon Date objects — noon keeps year-shifting arithmetic
 * (below) safely away from any DST-adjacent date-boundary surprises.
 */
function trailingWindow(year: number, month: number, day: number, windowDays: number): Date[] {
  const endUTC = Date.UTC(year, month - 1, day, 12);
  return Array.from({ length: windowDays }, (_, i) => new Date(endUTC - (windowDays - 1 - i) * 86_400_000));
}

function shiftYears(d: Date, deltaYears: number): Date {
  return new Date(Date.UTC(d.getUTCFullYear() + deltaYears, d.getUTCMonth(), d.getUTCDate(), 12));
}

function fmtShort(d: Date): string {
  return new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", timeZone: "UTC" }).format(d);
}

function valueAt(d: Date, matrix: YoYMatrix): number | null {
  const row = matrix[String(d.getUTCFullYear())];
  if (!row) return null;
  const v = row[String(doyOf(d.getUTCFullYear(), d.getUTCMonth() + 1, d.getUTCDate()))];
  return v != null && Number.isFinite(v) ? v : null;
}

export interface QuarterRankingOptions {
  windowDays?: number;
  /** Live day-of-month means for the current year, layered over the aggregate — same as month-to-date. */
  live?: MonthDailyMeans | null;
  minCoverage?: number;
}

/**
 * Rank each year's trailing `windowDays`-day mean (ending "now" for the
 * current year, the same calendar window shifted back a whole number of
 * years for every other year) — a rolling quarter rather than a
 * calendar-month-to-date, so the sample is 3x larger and less sensitive to a
 * single warm or cold week. Real dates drive the window (not day-of-year
 * indices), so it crosses a year boundary correctly when "now" falls early
 * in the year (e.g. a window from November into January).
 *
 * Every year is compared over only the dates the current year actually has
 * — same principle as computeMonthRanking's `comparedDays`, just over a
 * date window instead of day-of-month numbers.
 */
export function computeQuarterRanking(
  agg: SensorAggregate,
  now: Date,
  options: QuarterRankingOptions = {}
): QuarterRanking | null {
  const { windowDays = QUARTER_WINDOW_DAYS, live = null, minCoverage = 0.5 } = options;
  const matrix = agg.yoy_matrix;
  const { year: currentYear, month: curMonth, day: curDay } = berlinPartsOf(now);
  const currentWindow = trailingWindow(currentYear, curMonth, curDay, windowDays);

  const currentValueAt = (d: Date): number | null => {
    if (live != null) {
      const y = d.getUTCFullYear(), m = d.getUTCMonth() + 1, dd = d.getUTCDate();
      if (y === live.year && m === live.month) {
        const v = live.days[String(dd)];
        if (v != null && Number.isFinite(v)) return v;
      }
    }
    return valueAt(d, matrix);
  };

  const currentValues = currentWindow.map(currentValueAt);
  const includedOffsets = currentValues.reduce<number[]>((acc, v, i) => {
    if (v != null) acc.push(i);
    return acc;
  }, []);
  if (includedOffsets.length === 0) return null;

  const years = [...new Set([...Object.keys(matrix).map(Number), currentYear])]
    .filter((y) => Number.isFinite(y))
    .sort((a, b) => a - b);

  const scored: Array<Omit<QuarterRankRow, "rank">> = [];
  for (const year of years) {
    const delta = year - currentYear;
    const vals: number[] = [];
    for (const i of includedOffsets) {
      const v = delta === 0 ? currentValues[i]! : valueAt(shiftYears(currentWindow[i]!, delta), matrix);
      if (v != null) vals.push(v);
    }
    if (vals.length < Math.ceil(includedOffsets.length * minCoverage)) continue;
    scored.push({
      year,
      mean: vals.reduce((a, b) => a + b, 0) / vals.length,
      days: vals.length,
      isCurrent: year === currentYear
    });
  }
  if (scored.length === 0) return null;

  const rows: QuarterRankRow[] = scored
    .sort((a, b) => b.mean - a.mean)
    .map((r, i) => ({ ...r, rank: i + 1 }));

  const first = currentWindow[0]!, last = currentWindow[currentWindow.length - 1]!;

  return {
    windowDays,
    windowLabel: `${fmtShort(first)} – ${fmtShort(last)}`,
    currentYear,
    rows,
    currentRow: rows.find((r) => r.isCurrent) ?? null,
    comparedDays: includedOffsets.length,
    partial: includedOffsets.length < windowDays
  };
}

export function buildQuarterRanking(
  agg: SensorAggregate | null,
  now: Date,
  live: MonthDailyMeans | null = null
): HTMLElement {
  // Reuses .month-rank's card chrome (border, padding, the :empty collapse,
  // the h4 margin) verbatim via the shared class, plus every row/bar/notes
  // class below — the two cards are the same widget over a different window.
  const container = el("section", { className: "month-rank quarter-rank" });
  if (!agg) return container;

  const ranking = computeQuarterRanking(agg, now, { live });
  if (!ranking) return container;

  const { windowLabel, rows, currentRow, comparedDays, windowDays, partial } = ranking;

  container.append(el("h4", { text: `📊 Rolling quarter (${windowLabel}), every year ranked` }));

  if (currentRow) {
    container.append(el("p", {
      className: "month-rank-summary",
      text: `The last ${comparedDays} days average ${currentRow.mean.toFixed(1)}°C — ` +
            `the ${ordinal(currentRow.rank)}-warmest such window of ${rows.length} years on record.`
    }));
  }

  const hi = rows[0]!.mean;
  const lo = rows[rows.length - 1]!.mean;
  const spread = hi - lo || 1;

  const list = el("div", { className: "month-rank-rows" });
  for (const r of rows) {
    const row = el("div", { className: r.isCurrent ? "month-rank-row is-current" : "month-rank-row" });
    const bar = el("span", { className: "month-rank-bar" });
    const pct = 8 + ((r.mean - lo) / spread) * 92;
    bar.setAttribute("style", `width: ${pct.toFixed(1)}%; background-color: ${r.isCurrent ? "#0d9488" : "#cbd5e1"};`);

    row.append(
      el("span", { className: "month-rank-pos", text: `${r.rank}.` }),
      el("span", { className: "month-rank-year", text: r.isCurrent ? `${r.year} (now)` : String(r.year) }),
      el("span", { className: "month-rank-track" }, [bar]),
      el("span", { className: "month-rank-val", text: `${r.mean.toFixed(1)}°C` })
    );
    list.append(row);
  }
  container.append(list);

  const notes = el("div", { className: "month-rank-notes" });
  if (partial) {
    const gaps = windowDays - comparedDays;
    notes.append(el("p", {
      text: `Rolling ${windowDays}-day window ending today: ${comparedDays} of ${windowDays} days have data ` +
            `(${gaps} missing). Every year is averaged over exactly these same calendar dates, shifted back one ` +
            `year at a time — not a fixed Jan/Apr/Jul/Oct quarter.`
    }));
  }
  notes.append(el("p", {
    text: "A larger sample than the month-to-date card above, so a single unusually hot or cold week moves this " +
          "ranking less. Same underlying daily means, same DWD/rooftop-box instrument caveat."
  }));
  container.append(notes);

  return container;
}
